import { OrderStatus } from '@/types/generated/common/trading'

const MAX_TRACKED_ORDERS = 1000

const STATUS_RANK = new Map<OrderStatus, number>([
  [OrderStatus.ORDER_STATUS_UNSPECIFIED, 0],
  [OrderStatus.ORDER_STATUS_PENDING, 1],
  [OrderStatus.ORDER_STATUS_OPEN, 2],
  [OrderStatus.ORDER_STATUS_PARTIALLY_FILLED, 3],
  [OrderStatus.ORDER_STATUS_FILLED, 4],
  [OrderStatus.ORDER_STATUS_CANCELLED, 4],
  [OrderStatus.ORDER_STATUS_REJECTED, 4],
])

const TERMINAL_STATUSES = new Set<OrderStatus>([
  OrderStatus.ORDER_STATUS_FILLED,
  OrderStatus.ORDER_STATUS_CANCELLED,
  OrderStatus.ORDER_STATUS_REJECTED,
])

function orderKey(accountId: string, orderId: string) {
  return `${accountId}:${orderId}`
}

/**
 * Tracks the last surfaced state for a bounded set of orders. Notification frames
 * can be delivered out of order, but a terminal order must never regress to an
 * OPEN/PENDING toast. Repeated partial fills remain visible because their fill
 * quantity can increase while the enum value stays unchanged.
 */
export function createOrderNotificationLifecycle(maxSize = MAX_TRACKED_ORDERS) {
  const statuses = new Map<string, OrderStatus>()

  return {
    accept(accountId: string, orderId: string, nextStatus: OrderStatus): boolean {
      if (!orderId) return true

      const key = orderKey(accountId, orderId)
      const previousStatus = statuses.get(key)

      if (previousStatus !== undefined) {
        if (TERMINAL_STATUSES.has(previousStatus)) return false

        const previousRank = STATUS_RANK.get(previousStatus) ?? 0
        const nextRank = STATUS_RANK.get(nextStatus) ?? 0
        if (nextRank < previousRank) return false
        if (
          nextStatus === previousStatus &&
          nextStatus !== OrderStatus.ORDER_STATUS_PARTIALLY_FILLED
        ) {
          return false
        }
      }

      // Refresh insertion order so the cap evicts the least recently updated order.
      statuses.delete(key)
      statuses.set(key, nextStatus)
      if (statuses.size > maxSize) {
        const oldestKey = statuses.keys().next().value
        if (oldestKey !== undefined) statuses.delete(oldestKey)
      }

      return true
    },
  }
}
